import React, { useCallback, useRef, useState } from 'react';

import { Toast, ToastMessage } from 'primereact/toast';
import {
  FormState,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger
} from 'react-hook-form';
import { NavigateFunction } from 'react-router-dom';

import { TaskService } from '../../../components/services/TaskService';
import { TaskClosingCodesTemplate } from '../../../constants/TaskClosingCodesTemplate';
import { getFieldFillingControlStandalone } from '../../../hooks/services/getFieldFillingControlStandalone';
import useClosingCodesService from '../../../hooks/services/useClosingCodesService';
import useStatusTransitionsService from '../../../hooks/services/useStatusTransitionsService';
import { updateFieldFillingControll } from '../../../state/schemaSlice/fieldFillingControllSlice';
import { AppDispatch, store } from '../../../state/store';
import {
  ActualOperation,
  DropdownObjectType,
  DropdownSelectorType,
  Employee,
  FieldFilling,
  Task,
  TaskClosingCode,
  TaskType
} from '../../../types/interfaces';
import { getFieldNameSchema } from '../../../utils/getFieldName';
import { buildTaskPayloadForSubmit } from '../../../utils/taskCard/buildTaskPayload';
import {
  getApiResponseErrorMessage,
  getTaskAssignmentErrorMessage,
  normalizeApiError
} from '../../../utils/taskCard/taskAssignmentErrors';
import { validateTaskCreateByTaskType } from '../../../utils/taskCard/taskTypeCreateValidation';
import { defaultNode, initState } from '../taskCardDefaults';

export type RecommendationDialogContext = 'save' | 'transition' | null;

export const TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST =
  'task_created_department_assignment_toast';

const hasDepartmentSelection = (department: Task['department']): boolean => {
  const key = department?.key;
  return key !== null && key !== undefined && String(key).trim().length > 0;
};

export type UseTaskCardSaveAndTransitionParams = {
  toast: React.RefObject<Toast | null>;
  navigate: NavigateFunction;
  dispatch: AppDispatch;
  fieldFillingControll: FieldFilling[];
  getFieldFillingControll: (id: number) => Promise<void>;
  getTaskApi: (id: number | undefined, onlyStatus?: boolean) => Promise<void>;
  setTriggerApiCall: React.Dispatch<React.SetStateAction<number | undefined>>;
  setValue: UseFormSetValue<Task>;
  setError: UseFormSetError<Task>;
  trigger: UseFormTrigger<Task>;
  formState: Pick<FormState<Task>, 'dirtyFields'>;
  handleSubmit: UseFormHandleSubmit<Task>;
  recommendedEmployeesCount: number | null;
  taskCodeCardRef: React.RefObject<{ handleCancel: () => void } | null>;
  actualOperations: ActualOperation[];
  plannedOperations: DropdownObjectType[];
  employees: Employee[];
  possibleStatusTransitions: { id: number; label: string }[] | undefined;
  lastStatusTransitionId: number | undefined;
  hasAttachments: boolean;
  taskId: number | string | undefined;
  ensureTaskTypeDetails: (taskTypeId: number) => Promise<TaskType | undefined>;
  selectedTaskTypeDetails: TaskType | null;
};

export function useTaskCardSaveAndTransition(
  params: UseTaskCardSaveAndTransitionParams
) {
  const {
    toast,
    navigate,
    dispatch,
    fieldFillingControll,
    getFieldFillingControll,
    getTaskApi,
    setTriggerApiCall,
    setValue,
    formState: { dirtyFields },
    setError,
    trigger,
    handleSubmit,
    recommendedEmployeesCount,
    taskCodeCardRef,
    actualOperations,
    plannedOperations,
    employees,
    possibleStatusTransitions,
    lastStatusTransitionId,
    hasAttachments,
    taskId,
    ensureTaskTypeDetails,
    selectedTaskTypeDetails
  } = params;

  const statusTransitionsService = useStatusTransitionsService();
  const closingCodesService = useClosingCodesService();

  const [attachmentsError, setAttachmentsError] = useState(false);
  const [isDepartmentDeleted, setIsDepartmentDeleted] = useState(false);
  const [closingCodes, setClosingCodes] = useState<TaskClosingCode[]>([]);
  const [closeCodeDialogVisible, setCloseCodeDialogVisible] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<DropdownSelectorType | null>(null);
  const [
    showEmployeesRecommendationDialog,
    setShowEmployeesRecommendationDialog
  ] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<Task | null>(null);
  const [pendingSaveTaskTypeDetails, setPendingSaveTaskTypeDetails] = useState<
    TaskType | undefined
  >(undefined);
  const [pendingTransitionRequest, setPendingTransitionRequest] = useState<{
    actionId: number;
    closingCodeId?: number;
  } | null>(null);
  const [recommendationDialogContext, setRecommendationDialogContext] =
    useState<RecommendationDialogContext>(null);

  const oldFieldFillingControllRef = useRef<FieldFilling[] | null>(null);
  const skipEmployeesRecommendationCheckRef = useRef(false);

  const showCreateValidationToast = useCallback(() => {
    toast.current?.show({
      severity: 'error',
      summary: 'Ошибка сохранения',
      detail: 'Проверьте корректность заполнения полей',
      life: 3000
    });
  }, [toast]);

  const validateCreateTask = useCallback(
    async (
      newData: Task
    ): Promise<{ isValid: boolean; taskTypeDetails?: TaskType }> => {
      const taskTypeId = Number(newData.taskType?.key);
      if (!taskTypeId || Number.isNaN(taskTypeId)) {
        setError('taskType', {
          type: 'value',
          message: 'Выберите тип наряда.'
        });
        showCreateValidationToast();
        return { isValid: false };
      }

      const currentDetailsId = Number(selectedTaskTypeDetails?.id);
      const details =
        selectedTaskTypeDetails &&
        !Number.isNaN(currentDetailsId) &&
        currentDetailsId === taskTypeId
          ? selectedTaskTypeDetails
          : await ensureTaskTypeDetails(taskTypeId);
      if (!details) {
        setError('taskType', {
          type: 'value',
          message: 'Не удалось загрузить правила валидации для типа наряда'
        });
        showCreateValidationToast();
        return { isValid: false };
      }

      const validationErrors = validateTaskCreateByTaskType(newData, details);
      if (validationErrors.length === 0) {
        return { isValid: true, taskTypeDetails: details };
      }

      validationErrors.forEach(({ field, message }) => {
        setError(field, {
          type: 'value',
          message
        });
      });
      showCreateValidationToast();
      return { isValid: false };
    },
    [
      ensureTaskTypeDetails,
      selectedTaskTypeDetails,
      setError,
      showCreateValidationToast
    ]
  );

  const submitTask = useCallback(
    async (newData: Task, createTaskTypeDetails?: TaskType) => {
      const dataId = newData.id;
      const externalIdErrorMessage = 'Введенный внешний № не уникален';
      const isIdExist =
        newData && dataId !== undefined && dataId !== null && dataId !== 0;
      const validationErrorToast: ToastMessage = {
        severity: 'error',
        summary: 'Ошибка валидации',
        detail: 'Введенный внешний № не уникален.',
        life: 3000
      };

      if (isIdExist) {
        const updateResponse = await TaskService.updateTask(newData);
        if (updateResponse.data.isSuccess && !updateResponse.data.error) {
          setIsDepartmentDeleted(false);
          window.opener?.postMessage(
            {
              type: 'UPDATE_SUCCESS',
              payload: { updatedDetail: [newData.id, newData] }
            },
            '*'
          );
          toast.current?.show({
            severity: 'success',
            summary: 'Сохранение',
            detail: 'Данные наряда успешно сохранены.',
            life: 3000
          });
          setTriggerApiCall(parseInt(newData.id.toString()));
        }

        if (
          !updateResponse.data.isSuccess &&
          updateResponse.data.error === 'SameExternalId'
        ) {
          setError('externalId', {
            type: 'value',
            message: externalIdErrorMessage
          });
          toast.current?.show(validationErrorToast);
        }
      }
      if (!isIdExist) {
        const shouldReportDepartmentAssignment =
          createTaskTypeDetails?.assignDepartmentOnSave === true &&
          !hasDepartmentSelection(newData.department);
        const createResponse = await TaskService.createTask(newData);
        if (createResponse.data.isSuccess && createResponse.data.value) {
          setIsDepartmentDeleted(false);
          const createdTask = createResponse.data.value;
          newData.id = createdTask.id;
          window.opener?.postMessage(
            {
              type: 'ADD_SUCCESS',
              payload: { onAddProps: newData }
            },
            '*'
          );
          localStorage.setItem('task_created_toast', newData.id.toString());
          if (shouldReportDepartmentAssignment) {
            localStorage.setItem(
              TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST,
              JSON.stringify({
                taskId: newData.id.toString(),
                assigned: hasDepartmentSelection(createdTask.department),
                departmentLabel: createdTask.department?.label ?? ''
              })
            );
          } else {
            localStorage.removeItem(TASK_CREATED_DEPARTMENT_ASSIGNMENT_TOAST);
          }
          navigate(`/${newData.id}`, { replace: true });
          setTriggerApiCall(Number(newData.id));
        }

        if (
          !createResponse.data.isSuccess &&
          createResponse.data.error === 'SameExternalId'
        ) {
          setError('externalId', {
            type: 'value',
            message: externalIdErrorMessage
          });
          toast.current?.show(validationErrorToast);
        }

        const createError = normalizeApiError(createResponse.data);
        const assignmentErrorMessage = getTaskAssignmentErrorMessage(
          createResponse.data
        );
        if (createResponse.status === 400 && assignmentErrorMessage) {
          if (createError === 'DepartmentAssignmentError') {
            setError('department', {
              type: 'value',
              message: assignmentErrorMessage
            });
          }
          if (
            createError === 'PlanningAlgorithmError' ||
            createError === 'planningAlgorithmError'
          ) {
            setError('taskType', {
              type: 'value',
              message: assignmentErrorMessage
            });
          }
          toast.current?.show({
            severity: 'error',
            summary: 'Ошибка регистрации наряда',
            detail: assignmentErrorMessage,
            life: 5000
          });
        }
      }
    },
    [navigate, setError, setTriggerApiCall, toast]
  );

  const onSubmit: SubmitHandler<Task> = useCallback(
    async (data) => {
      const newData = buildTaskPayloadForSubmit(
        data,
        plannedOperations,
        actualOperations
      );

      const isIdExist =
        newData.id !== undefined && newData.id !== null && newData.id !== 0;

      let createTaskTypeDetails: TaskType | undefined;

      if (!isIdExist) {
        const validationResult = await validateCreateTask(newData);
        if (!validationResult.isValid) return;
        createTaskTypeDetails = validationResult.taskTypeDetails;
      }

      const selectedEmployeesCount = Array.isArray(newData.employees)
        ? newData.employees.length
        : 0;
      const hasEmployeesRecommendationMismatch =
        recommendedEmployeesCount !== null &&
        selectedEmployeesCount !== recommendedEmployeesCount;

      const isEmployeesDirty = !!dirtyFields?.employees;

      if (
        hasEmployeesRecommendationMismatch &&
        isEmployeesDirty &&
        !skipEmployeesRecommendationCheckRef.current
      ) {
        setPendingSaveData(newData);
        setPendingSaveTaskTypeDetails(createTaskTypeDetails);
        setRecommendationDialogContext('save');
        setShowEmployeesRecommendationDialog(true);
        return;
      }

      await submitTask(newData, createTaskTypeDetails);
    },
    [
      actualOperations,
      plannedOperations,
      recommendedEmployeesCount,
      submitTask,
      validateCreateTask
    ]
  );

  const onError = useCallback(
    (errors: unknown) => {
      if (toast.current && errors) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка сохранения',
          detail: 'Проверьте корректность заполнения полей',
          life: 3000
        });
      }
    },
    [toast]
  );

  const handleFieldFunctions = useCallback(
    async (newfieldFillingControll: FieldFilling[]) => {
      for (const fieldDetails of newfieldFillingControll) {
        const fieldSchema = getFieldNameSchema(fieldDetails.fieldType);

        if (fieldSchema) {
          if (
            fieldDetails.currentFunctions.some(
              (func) => func.id.toString() === '1'
            )
          ) {
            const currentDate = new Date().toISOString();
            if (
              [
                'actualStartDate',
                'plannedEndDate',
                'actualEndDate',
                'plannedStartDate'
              ].includes(fieldSchema)
            ) {
              setValue(fieldSchema as keyof Task, currentDate, {
                shouldDirty: true,
                shouldValidate: true
              });
            }
          }
        }
      }
    },
    [setValue]
  );

  const performStatusTransition = useCallback(
    async (
      actionId: number,
      closingCodeId: number | undefined = undefined,
      skipEmployeesRecommendationCheck: boolean = false
    ) => {
      const selectedEmployeesCount = Array.isArray(employees)
        ? employees.length
        : 0;
      const hasEmployeesRecommendationMismatch =
        recommendedEmployeesCount !== null &&
        selectedEmployeesCount !== recommendedEmployeesCount;

      const isEmployeesDirty = !!dirtyFields?.employees;

      if (
        !skipEmployeesRecommendationCheck &&
        hasEmployeesRecommendationMismatch &&
        isEmployeesDirty
      ) {
        setPendingTransitionRequest({ actionId, closingCodeId });
        setRecommendationDialogContext('transition');
        setShowEmployeesRecommendationDialog(true);
        return;
      }

      const selectedActionFromList = possibleStatusTransitions?.find(
        (a) => a.id === actionId
      );

      const transitionResponse = await TaskService.doTaskTransition({
        taskId: parseInt(taskId?.toString() ?? '0'),
        transitionId: actionId,
        closingCodeId: closingCodeId
      });

      const transitionError = getApiResponseErrorMessage(
        transitionResponse,
        'Не удалось выполнить переход по статусу. Попробуйте ещё раз.'
      );

      if (transitionError) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: transitionError,
          life: 5000
        });

        if (oldFieldFillingControllRef.current) {
          dispatch(
            updateFieldFillingControll(oldFieldFillingControllRef.current)
          );
        }
        lastStatusTransitionId &&
          (await getFieldFillingControll(lastStatusTransitionId));
        return;
      }

      const newfieldFillingControll = store.getState().fieldFillingControll;
      await handleFieldFunctions(newfieldFillingControll as FieldFilling[]);

      if (newfieldFillingControll) {
        newfieldFillingControll.forEach((fieldDetails) => {
          if (fieldDetails.isResettable) {
            const fieldSchema = getFieldNameSchema(fieldDetails.fieldType);
            if (fieldSchema) {
              if (fieldSchema === 'employees') {
                setValue(fieldSchema, [], {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'asset') {
                setValue(fieldSchema, defaultNode, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'department') {
                setValue(fieldSchema, defaultNode, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'taskType') {
                setValue(fieldSchema, defaultNode, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'plannedOperations') {
                setValue('plannedOperations', [], {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'actualOperations') {
                setValue('actualOperations', [], {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else if (fieldSchema === 'interval') {
                setValue('interval', undefined, {
                  shouldDirty: true,
                  shouldValidate: true
                });
              } else {
                setValue(fieldSchema as keyof typeof initState, '', {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
            }
          }
        });
      }

      skipEmployeesRecommendationCheckRef.current = true;
      try {
        await handleSubmit(onSubmit, onError)();
      } finally {
        skipEmployeesRecommendationCheckRef.current = false;
      }
      await getTaskApi(parseInt(taskId?.toString() ?? '0'), true);

      window.opener?.postMessage(
        {
          type: 'TRANSITION_SUCCESS',
          payload: { updatedDetailAction: actionId }
        },
        '*'
      );
      if (toast.current) {
        toast.current.show({
          severity: 'info',
          summary: 'Выбор действия',
          detail: `Вы выбрали действие: ${selectedActionFromList?.label}`,
          life: 3000
        });
      }
    },
    [
      employees,
      recommendedEmployeesCount,
      possibleStatusTransitions,
      taskId,
      setValue,
      handleSubmit,
      onSubmit,
      onError,
      getTaskApi,
      toast,
      handleFieldFunctions,
      dispatch,
      getFieldFillingControll,
      lastStatusTransitionId
    ]
  );

  const handleActionSelect = useCallback(
    async (actionId: number) => {
      oldFieldFillingControllRef.current = fieldFillingControll;
      const selectedActionItem = possibleStatusTransitions?.find(
        (a) => a.id === actionId
      );
      const serviceResult = closingCodesService.getByStatus(actionId);
      const result = await serviceResult.result;

      const newfieldFillingControll = await getFieldFillingControlStandalone(
        actionId,
        statusTransitionsService
      );

      const hasAttachmentsField = newfieldFillingControll.some(
        (field) => field.fieldType === 11 && field.isActive && field.isRequired
      );

      if (hasAttachmentsField && !hasAttachments) {
        setAttachmentsError(true);

        if (toast.current) {
          toast.current.show({
            severity: 'error',
            summary: 'Ошибка',
            detail:
              'Для выполнения этого действия необходимо добавить хотя бы 1 вложение.',
            life: 3000
          });
        }

        if (oldFieldFillingControllRef.current) {
          dispatch(
            updateFieldFillingControll(oldFieldFillingControllRef.current)
          );
        }
        lastStatusTransitionId &&
          (await getFieldFillingControll(lastStatusTransitionId));
        return;
      }

      setAttachmentsError(false);

      dispatch(updateFieldFillingControll(newfieldFillingControll));
      await getFieldFillingControll(actionId);
      const isValid = await trigger();
      trigger('actualEndDate');

      if (isValid) {
        if (result && result?.length > 0 && selectedActionItem) {
          setClosingCodes(result);
          setSelectedAction(selectedActionItem);
          setValue('selectedAction', selectedActionItem);
          setCloseCodeDialogVisible(true);
        } else {
          setValue('closeCode', TaskClosingCodesTemplate);
          await performStatusTransition(actionId);
        }
      } else {
        if (oldFieldFillingControllRef.current) {
          dispatch(
            updateFieldFillingControll(oldFieldFillingControllRef.current)
          );
        }
        lastStatusTransitionId &&
          (await getFieldFillingControll(lastStatusTransitionId));
        if (toast.current) {
          toast.current.show({
            severity: 'error',
            summary: 'Ошибка',
            detail:
              'Пожалуйста, заполните все обязательные поля перед изменением статуса.',
            life: 3000
          });
        }
      }
    },
    [
      fieldFillingControll,
      closingCodesService,
      statusTransitionsService,
      possibleStatusTransitions,
      hasAttachments,
      dispatch,
      toast,
      lastStatusTransitionId,
      getFieldFillingControll,
      trigger,
      setValue,
      performStatusTransition
    ]
  );

  const onSubmitSchemaLoad = useCallback(async () => {
    if (lastStatusTransitionId !== undefined) {
      const newfieldFillingControll = await getFieldFillingControlStandalone(
        lastStatusTransitionId,
        statusTransitionsService
      );
      dispatch(updateFieldFillingControll(newfieldFillingControll));
      await getFieldFillingControll(lastStatusTransitionId);
      await trigger();
    }
    handleSubmit(onSubmit, onError)();
  }, [
    lastStatusTransitionId,
    statusTransitionsService,
    dispatch,
    getFieldFillingControll,
    trigger,
    handleSubmit,
    onSubmit,
    onError
  ]);

  const handleCodeDialogSave = useCallback(
    async (selectedCloseCode: TaskClosingCode) => {
      setValue('closeCode', selectedCloseCode);

      setCloseCodeDialogVisible(false);
      if (selectedAction && typeof selectedAction.id === 'number') {
        await performStatusTransition(
          selectedAction.id,
          selectedCloseCode.id
            ? parseInt(selectedCloseCode.id.toString())
            : undefined
        );
      }
    },
    [setValue, selectedAction, performStatusTransition]
  );

  const saveDialog = useCallback(() => {
    setCloseCodeDialogVisible(false);
  }, []);

  const closeClosingCodesDialog = useCallback(() => {
    setCloseCodeDialogVisible(false);
    if (oldFieldFillingControllRef.current) {
      dispatch(updateFieldFillingControll(oldFieldFillingControllRef.current));
    }
  }, [dispatch]);

  const onHideTaskCodeCard = useCallback(() => {
    if (taskCodeCardRef.current !== null) {
      taskCodeCardRef.current.handleCancel();
      if (oldFieldFillingControllRef.current) {
        dispatch(
          updateFieldFillingControll(oldFieldFillingControllRef.current)
        );
      }
    }
  }, [dispatch, taskCodeCardRef]);

  const dismissEmployeesRecommendationDialog = useCallback(() => {
    setShowEmployeesRecommendationDialog(false);
    setPendingSaveData(null);
    setPendingSaveTaskTypeDetails(undefined);
    setPendingTransitionRequest(null);
    setRecommendationDialogContext(null);
  }, []);

  const confirmEmployeesRecommendation = useCallback(async () => {
    if (recommendationDialogContext === 'save' && pendingSaveData) {
      await submitTask(pendingSaveData, pendingSaveTaskTypeDetails);
    }
    if (
      recommendationDialogContext === 'transition' &&
      pendingTransitionRequest
    ) {
      await performStatusTransition(
        pendingTransitionRequest.actionId,
        pendingTransitionRequest.closingCodeId,
        true
      );
    }
    if (recommendationDialogContext === 'save' && !pendingSaveData) {
      setShowEmployeesRecommendationDialog(false);
      return;
    }
    setShowEmployeesRecommendationDialog(false);
    setPendingSaveData(null);
    setPendingSaveTaskTypeDetails(undefined);
    setPendingTransitionRequest(null);
    setRecommendationDialogContext(null);
  }, [
    recommendationDialogContext,
    pendingSaveData,
    pendingSaveTaskTypeDetails,
    pendingTransitionRequest,
    submitTask,
    performStatusTransition
  ]);

  return {
    attachmentsError,
    closingCodes,
    closeCodeDialogVisible,
    selectedAction,
    showEmployeesRecommendationDialog,
    pendingSaveData,
    pendingTransitionRequest,
    recommendationDialogContext,
    submitTask,
    onSubmit,
    onError,
    onSubmitSchemaLoad,
    handleActionSelect,
    performStatusTransition,
    handleCodeDialogSave,
    saveDialog,
    closeClosingCodesDialog,
    onHideTaskCodeCard,
    dismissEmployeesRecommendationDialog,
    confirmEmployeesRecommendation,
    isDepartmentDeleted,
    setIsDepartmentDeleted
  };
}
